import React, { memo, useState, useCallback, useEffect, useMemo, Fragment } from 'react';
import { FormCheckbox, FormAjaxSelect, FormInput } from '@core/components/form';
import { useObservable, useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useBloc } from '@core/utils/bloc';
import { GlossaryBlockWidgetForm, GlossaryBlockVariation, GlossaryForm } from './GlossaryBlock';
import { GlossaryBlockBloc } from './GlossaryBlockBloc';
import { GlossaryDefinitionResponse, GlossaryResponse } from '@business/entities/glossary';
import { Card, CardBody, Col, Row, Label } from 'reactstrap';
import { DefinitionForm } from '../LibraryBlock';
import { GlossaryAPI } from '@business/api/glossary';
import { map, switchMap } from 'rxjs/operators';
import { CommonService, HTMLService } from '@business/services';
import { of } from 'rxjs';
import { R } from '@core/utils/r';
import { SortableList } from '@modules/common/Sortable';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { useDidMount } from '@core/utils/hooks/react';

export interface GlossaryBlockSettingsProps {
  form: GlossaryBlockWidgetForm;
  variation: GlossaryBlockVariation;
  id: string;
  lessonId?: string;
  bloc: GlossaryBlockBloc;
}

export const GlossaryBlockSettings = memo((props: GlossaryBlockSettingsProps) => {
  const { form, lessonId, id, bloc } = props;
  const glossary = useObservable(form.controls.glossary.items$, []);
  const glossaryValues = useObservable(form.controls.glossary.value$, []);
  const glossaryItems = useObservable(bloc.items$, []);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newGlossaries = R.move(glossaryValues, oldIndex, newIndex);
      form.patchValue({
        glossary: newGlossaries,
      });
    },
    [form, glossaryValues],
  );

  return (
    <Fragment>
      <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
        {glossary.map((ctrl, index) => {
          const item = R.first(R.filter(glossaryItems, (g: GlossaryResponse) => g.id == ctrl.value.search)) as GlossaryResponse;
          return (
            <SortableGlossaryItem
              key={ctrl.uid}
              index={index}
              ctrl={ctrl}
              form={form}
              i={index}
              lessonId={lessonId}
              id={id}
              bloc={bloc}
              definitions={item ? item.definitions : []}
            />
          );
        })}
      </SortableList>
    </Fragment>
  );
});

interface GlossaryItemProps {
  i: number;
  ctrl: GlossaryForm;
  form: GlossaryBlockWidgetForm;
  lessonId?: string;
  id: string;
  bloc: GlossaryBlockBloc;
  definitions: GlossaryDefinitionResponse[];
}

const GlossaryItem = memo((props: GlossaryItemProps) => {
  const { ctrl, form, id, lessonId, i, bloc, definitions } = props;
  const formValue = useNonNilObservable(ctrl.value$);
  const [glossaries, setGlossaries] = useState<GlossaryResponse[]>([]);
  const definitionItems = useObservable(ctrl.controls.definitions.items$, []);
  const DragHandle = SortableHandle(() => (
    <i className="fas fa-grip-vertical" style={{ cursor: 'move' }}>
      {' '}
    </i>
  ));

  const initialValue = useMemo(() => {
    if (formValue.search && formValue.searchValue) {
      return { label: formValue.searchValue, value: formValue.search };
    }
    return undefined;
  }, [formValue.search, formValue.searchValue]);

  const onSearch = useCallback((search: string) => {
    return GlossaryAPI.searchGlossary(search).pipe(
      map(items => {
        setGlossaries(items);
        return CommonService.selectItems(items, 'title', 'id');
      }),
    );
  }, []);

  const onUpdate = useCallback(() => {
    ctrl.patchValue({
      definitions: R.map(definitions || [], d => ({
        definitionId: d.id,
        option: R.find(formValue.definitions || [], f => f.definitionId == d.id)?.option || false,
      })),
    });
  }, [ctrl, definitions, formValue.definitions]);

  useDidMount(() => {
    onUpdate();
  });

  const onFormChange = useCallback(() => {
    setTimeout(() => {
      if (lessonId) {
        return bloc
          .onReload(lessonId)
          .pipe(
            switchMap(res => {
              const blocks = R.get(res, 'data.blocks');
              if (blocks && !!blocks.length) {
                const block = R.first(R.filter(blocks, b => b.id == id));
                if (block) {
                  const data = block.data;
                  ctrl.patchValue({ text: data.text });
                }
              }
              return of(null);
            }),
          )
          .subscribe();
      }
    }, 6000);
  }, [lessonId, bloc, id, ctrl]);

  const onChange = useCallback(
    (value: string) => {
      const glossary = R.first(R.filter(glossaries, (g: GlossaryResponse) => g.id == value)) as GlossaryResponse;
      //   setDefinitions(glossary?.definitions || []);
      ctrl.patchValue({
        searchValue: glossary?.title,
        heading: glossary?.title,
        definitions: R.map(glossary.definitions || [], d => ({
          definitionId: d.id,
          option: false,
        })),
      });
      onFormChange();
      onUpdate();
    },
    [ctrl, glossaries, onFormChange, onUpdate],
  );

  const controlKey = useMemo(() => {
    if (initialValue) {
      return initialValue.label;
    }
    return R.random(1, 1000).toString();
  }, [initialValue]);

  return (
    <div>
      <label className="text-muted" style={{ opacity: 0.5 }}>
        {' '}
        Word #{i + 1}{' '}
      </label>
      <Row key={`tab-${i} mb-2 mt-2`}>
        <Col md={1}>
          <DragHandle />
        </Col>
        <Col md={8}>
          <FormAjaxSelect
            key={controlKey}
            initalValue={initialValue}
            control={ctrl.controls.search}
            callback={search => onSearch(search)}
            onChange={onChange}
          />
          {definitionItems.map((childControl, i) => {
            const def = R.first(R.filter(definitions, d => d.id == childControl.value.definitionId)) as GlossaryDefinitionResponse;
            return <GlossaryDefinitionItem ctrl={childControl} key={i} definition={def} onDefinitionClick={onFormChange} />;
          })}
        </Col>
        <Col md={2}>
          {!i && (
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.glossary.push()}>
              Add Word
            </a>
          )}
          {!!i && (
            <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => form.controls.glossary.removeAt(i)}>
              <i className="bx bx-x"> </i>
            </a>
          )}
        </Col>
      </Row>
      <hr />
    </div>
  );
});

export const SortableGlossaryItem = SortableElement(GlossaryItem);

export interface GlossaryDefinitionItemProps {
  ctrl: DefinitionForm;
  definition?: GlossaryDefinitionResponse;
  onDefinitionClick(): void;
}

export const GlossaryDefinitionItem = memo((props: GlossaryDefinitionItemProps) => {
  const { definition, ctrl, onDefinitionClick } = props;

  return definition ? (
    <Card className="mb-2 ha-item" style={{ cursor: 'pointer' }}>
      <CardBody>
        <FormCheckbox control={ctrl.controls.option} noPadding onChange={onDefinitionClick}>
          <div>{HTMLService.toReact(definition?.body || '<p></p>')}</div>
        </FormCheckbox>
      </CardBody>
    </Card>
  ) : null;
});
